import { CatalogResourceDc, ExtendedProjectInfoDc } from "@evergis/api";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RoutePanelName } from "../../appConfig/types";
import { ProjectInfoBlock } from "../../components/Dialogs/EditResource/components/Project/Info/types";
import { goToMainBase } from "../../ducks/location/location";
import { getProject } from "../../ducks/project/selectors";
import { createProject } from "../../resources/projects";
import { useNotification } from "../notifications/useNotification";

type SaveProjectMeta = {
  loading: boolean;
  project: Partial<ExtendedProjectInfoDc>;
};

export const useProjectSave = (
  updateResource: CatalogResourceDc,
  info: ProjectInfoBlock[],
): [SaveProjectMeta, VoidFunction] => {
  const { t } = useTranslation("dialogs");

  const dispatch = useDispatch();
  const project = useSelector(getProject);
  const [create, { data: createdProject, loading: projectCreating, error: projectCreateError }] = createProject.use();

  const onApply = useCallback(() => {
    create({
      input: {
        ...(project as ExtendedProjectInfoDc),
        name: updateResource.systemName,
        description: updateResource.description,
        alias: updateResource.name,
        parentId: updateResource.parentId,
        content: { ...(project as ExtendedProjectInfoDc).content, projectInfo: info },
      },
    });
  }, [project, create, updateResource, info]);

  useEffect(() => {
    if (createdProject && !projectCreating) {
      dispatch(goToMainBase({ project: createdProject.name, panel: RoutePanelName.Layers }));
    }
  }, [createdProject, projectCreating]);

  useNotification(createdProject && !projectCreating, {
    success: true,
    title: t("newProject.mapCreated"),
    description: updateResource.name,
  });

  useNotification(projectCreateError, {
    error: true,
    title: t("newProject.couldNotCreateMap"),
    description: updateResource.name,
  });

  return [{ loading: projectCreating, project }, onApply];
};
